import React, { useEffect, useImperativeHandle, forwardRef } from "react";
import { useDispatch, useSelector } from "../../../store";
import { Box, Grid2, Typography } from "@mui/material";
import { useForm, SubmitHandler } from "react-hook-form";
import FormTextField from "../../forms/FormTextField";
import FormAutocomplete from "../../forms/FormAutocomplete";
import User from "../../../types/user";
import { getDashboardList } from "../../../slices/dashboard";
import { Info } from "@mui/icons-material";
import { useSkeletonLoader } from "../../../hooks/useSkeletonLoader";
import checkRole, { UserRoles } from "../../../helpers/checkRole";
import FormPhoneField from "../../forms/FormPhoneField";
import FormCheckbox from "../../forms/FormCheckbox";
import theme from "../../../theme";

interface FormProps {
  type: "add" | "edit" | "profile";
  onSubmit: SubmitHandler<User>;
}

export type UserFormSubmitHandler = () => void;

const AddEditUser = forwardRef<UserFormSubmitHandler, FormProps>(
  (props, ref) => {
    const { type, onSubmit } = props;
    const dispatch = useDispatch();

    const { loggedInUser } = useSelector((store) => store.auth);
    const { user, formStatus } = useSelector((store) => store.users);
    const { dashboardOptions } = useSelector((store) => store.dashboard);

    const { handleSubmit, control, reset } = useForm<User>({
      mode: "onChange",
      defaultValues:
        type === "profile"
          ? {
              ...loggedInUser,
              textEnabled:
                loggedInUser?.verificationPending === true
                  ? loggedInUser?.verificationPending
                  : loggedInUser?.textEnabled,
            }
          : user,
    });

    useImperativeHandle(ref, () => handleSubmit(onSubmit));
    const { loadWithSkeleton } = useSkeletonLoader(formStatus === "loading");

    useEffect(() => {
      dispatch(getDashboardList());
    }, [dispatch]);

    useEffect(() => {
      if (type === "profile" && !!loggedInUser) {
        reset({
          ...loggedInUser,
          textEnabled:
            loggedInUser?.verificationPending === true
              ? loggedInUser?.verificationPending
              : loggedInUser?.textEnabled,
        });
      } else if (!!user) {
        reset(user);
      }
    }, [reset, user, loggedInUser, type]);

    return (
      <Box display="flex" flexDirection="row" width="100%">
        <Box display="flex" flexDirection="column" width="100%">
          <form>
            <Grid2 container spacing={2}>
              {type === "profile" && (
                <Box display="flex" flexDirection="row" width="100%" mt={2}>
                  <Typography fontWeight="bold">Global Settings</Typography>
                </Box>
              )}
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormTextField
                    label="First Name"
                    control={control}
                    fieldName="firstName"
                    disabled={type !== "profile"}
                  />
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormTextField
                    control={control}
                    fieldName="lastName"
                    label="Last Name"
                    disabled={type !== "profile"}
                  />
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormTextField
                    control={control}
                    fieldName="email"
                    label="Contact Email"
                    disabled={type !== "profile"}
                    type="email"
                  />
                )}
              </Grid2>
              {type === "profile" && (
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  {loadWithSkeleton(
                    <FormAutocomplete
                      control={control}
                      fieldName="roles"
                      freeSolo
                      options={[]}
                      multiple
                      label="User Roles"
                      disabled
                    />
                  )}
                </Grid2>
              )}
              {type === "profile" && (
                <Box display="flex" flexDirection="row" width="100%" mt={2}>
                  <Typography fontWeight="bold">Detector Settings</Typography>
                </Box>
              )}
              <Grid2
                size={{ xs: 12, sm: 6 }}
                display="flex"
                flexDirection="column"
                gap={1}
                alignItems="center"
              >
                <Grid2
                  size={{ xs: 12 }}
                  display="flex"
                  flexDirection="row"
                  gap={1}
                  alignItems="center"
                >
                  {loadWithSkeleton(
                    <FormAutocomplete
                      control={control}
                      fieldName="dashboard"
                      options={dashboardOptions || []}
                      required
                      label="Home Dashboard"
                      staticFilter
                      disabled={
                        type === "profile" &&
                        !checkRole(loggedInUser!, UserRoles.AnyAdmin)
                      }
                    />
                  )}
                </Grid2>
                {type !== "profile" && (
                  <Grid2
                    size={{ xs: 12 }}
                    display="flex"
                    flexDirection="row"
                    gap={1}
                    alignItems="center"
                    paddingX={4}
                  >
                    <Info color="primary" />
                    <Typography variant="caption" color="primary">
                      Select a home dashboard to grant the user access to it and
                      all nested dashboards below.
                    </Typography>
                  </Grid2>
                )}
              </Grid2>
              {type === "profile" && (
                <>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    {loadWithSkeleton(
                      <FormPhoneField
                        control={control}
                        fieldName="phoneNumber"
                        label="Phone Number"
                        disabled={type !== "profile"}
                      />
                    )}
                  </Grid2>
                  <Grid2
                    size={{ xs: 12 }}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={3}
                  >
                    {loggedInUser?.verificationPending && (
                      <Typography variant="overline" color="error">
                        Phone Number Verification Pending
                      </Typography>
                    )}
                    {loadWithSkeleton(
                      <FormCheckbox
                        control={control}
                        fieldName="textEnabled"
                        label="Text Me SV Pro Alerts"
                        disabled={type !== "profile"}
                      />
                    )}
                  </Grid2>
                  <Grid2
                    size={{ xs: 12 }}
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    gap={2}
                  >
                    <Typography
                      variant="caption"
                      textAlign="right"
                      color="info"
                      fontWeight="bold"
                    >
                      By checking this box, you agree to receive transactional
                      text messages from SecureVizual Pro (Secure Vizual, LLC)
                      with security alerts and scheduled reports at the mobile
                      number you provide. Message frequency varies based on your
                      alert activity and group membership. Message and data
                      rates may apply. Reply STOP to cancel, HELP for help. See
                      our{" "}
                      <a
                        href={`${process.env.REACT_APP_AUTH_URL}/terms`}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: theme.palette.primary.main,
                          fontWeight: "bold",
                        }}
                      >
                        Terms
                      </a>{" "}
                      and{" "}
                      <a
                        href={`${process.env.REACT_APP_AUTH_URL}/privacy`}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: theme.palette.primary.main,
                          fontWeight: "bold",
                        }}
                      >
                        Privacy Policy
                      </a>
                      .
                    </Typography>
                    <Typography
                      variant="caption"
                      textAlign="right"
                      color="info"
                      fontWeight="bold"
                    >
                      You will only receive texts if an admin adds you to a
                      Notification Group.{" "}
                      <i>
                        Carriers are not liable for delayed or undelivered
                        messages.
                      </i>
                    </Typography>
                  </Grid2>
                </>
              )}
            </Grid2>
          </form>
        </Box>
      </Box>
    );
  }
);

export default AddEditUser;
